// Arabic legal pages — Privacy, Terms, Cookie. Structure mirrors the
// finalized English legal pages section-by-section so cross-referencing
// between languages is straightforward.
import { Seo } from "@/components/Seo";
import { ArPageHero, ArBlock } from "@/pages/ar/_ArShared";

const LAST_UPDATED = "2 سبتمبر 2026";
const CR_NUMBER = "1653208";

const LAWFUL_BASIS_ROWS_AR = [
  ["الرد على استفساركم وإعداد عرض", "للرد والتسعير", "موافقتكم، والخطوات المتخذة بطلبكم قبل التعاقد"],
  ["تقديم الخدمات التي تعاقدتم معنا عليها", "لتنفيذ العقد", "تنفيذ عقد"],
  ["إرسال تحديثات أو دليل مطلوب", "لإبقائكم على اطلاع", "موافقتكم، القابلة للسحب في أي وقت"],
  ["تقييم طلب توظيف", "للنظر في ترشيحكم", "خطوات متخذة بطلبكم"],
  ["تشغيل الموقع وتأمينه وتحسينه", "لإبقاء الموقع يعمل وآمنًا", "مصلحتنا المشروعة في تشغيل أعمالنا بأمان"],
  ["الاحتفاظ بسجلات محاسبية وضريبية", "لأن ذلك مطلوب منا", "الامتثال لالتزام قانوني"],
  ["كشف ومنع الاحتيال أو سوء الاستخدام", "لحماية أنظمتنا وعملائنا", "مصلحتنا المشروعة في الأمان"],
];

const RETENTION_ROWS_AR = [
  ["استفسارات لم تتحول إلى مشاريع", "24 شهرًا من آخر تواصل"],
  ["سجلات العملاء والمراسلات", "مدة الارتباط، ثم 10 سنوات"],
  ["السجلات المحاسبية والضريبية", "وفق ما يطلبه القانون العُماني"],
  ["طلبات التوظيف (غير الناجحة)", "12 شهرًا، ما لم تطلبوا الاحتفاظ بها أطول"],
  ["الاشتراكات", "حتى إلغاء الاشتراك"],
  ["تحليلات الموقع", "26 شهرًا"],
  ["سجلات الخادم والأمان", "12 شهرًا"],
];

function Table({ headers, rows }) {
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full min-w-[520px] border-collapse text-sm">
        <thead>
          <tr className="bg-wafa-cream/60 text-start text-wafa-deep">
            {headers.map((h, i) => <th key={i} className="border border-wafa-deep/10 p-3 font-semibold">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="align-top">
              {r.map((c, j) => <td key={j} className="border border-wafa-deep/10 p-3">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LegalShell({ title, description, h1, intro, testid, children }) {
  return (
    <div data-testid={testid}>
      <Seo title={title} description={description} />
      <ArPageHero eyebrow="قانوني" h1={h1} sub={intro} breadcrumbs={[{ label: "الرئيسية", to: "/ar" }, { label: h1 }]} />
      <ArBlock tint="white">
        <article className="mx-auto max-w-3xl space-y-10 text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
          {children}
        </article>
      </ArBlock>
    </div>
  );
}

function H2({ children }) { return <h2 className="font-heading text-2xl font-semibold text-wafa-deep">{children}</h2>; }
function UL({ items }) {
  return (
    <ul className="mt-3 list-disc space-y-1 ps-6">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}

export function PrivacyPolicyAr() {
  return (
    <LegalShell
      testid="privacy-policy-ar"
      title="سياسة الخصوصية"
      description="كيف تجمع وفا سوليوشنز LLC البيانات الشخصية وتستخدمها وتحميها بموجب قانون حماية البيانات العُماني."
      h1="سياسة الخصوصية"
      intro={`آخر تحديث: ${LAST_UPDATED} · سارية اعتبارًا من: ${LAST_UPDATED}`}
    >
      <section>
        <H2>1. من نحن</H2>
        <p className="mt-4">
          وفا سوليوشنز LLC ("وفا سوليوشنز"، "نحن") شركة تقنية مسجّلة في سلطنة عُمان تحت رقم السجل التجاري {CR_NUMBER}، والشريك التقني لمجموعة Altalib. لأغراض قانون حماية البيانات الشخصية الصادر بالمرسوم السلطاني رقم 6/2022 ولائحته التنفيذية الصادرة بالقرار الوزاري رقم 34/2024، نحن مسؤولو معالجة البيانات الشخصية الموصوفة في هذه السياسة. تشرح هذه السياسة ما نجمعه، ولماذا، وكم نحتفظ به، وما حقوقكم.
        </p>
      </section>

      <section>
        <H2>2. التواصل معنا بشأن بياناتكم</H2>
        <p className="mt-4">
          لأي سؤال عن هذه السياسة أو بياناتكم، أو لممارسة حقوقكم، تواصلوا معنا على <a href="mailto:support@wafa.om" className="underline">support@wafa.om</a>. نحن بصدد التعيين الرسمي لمسؤول حماية بيانات مُسمّى؛ وحتى ذلك الحين، تُعالج جميع استفسارات حماية البيانات فريقنا على العنوان أعلاه.
        </p>
      </section>

      <section>
        <H2>3. ما نجمعه</H2>
        <ul className="mt-4 space-y-3">
          <li><strong>عند تواصلكم معنا أو طلب عرض —</strong> اسمكم، اسم الشركة، البريد الإلكتروني، الهاتف أو واتساب، وما تختارون قوله عن مشروعكم، بما في ذلك أي ملف تُرفقونه.</li>
          <li><strong>عند الاشتراك أو تنزيل دليل —</strong> اسمكم وبريدكم الإلكتروني.</li>
          <li><strong>عند التقدّم لوظيفة —</strong> اسمكم، بيانات التواصل، السيرة الذاتية، تاريخ العمل، وما تُرسلونه لنا.</li>
          <li><strong>عند زيارة موقعنا —</strong> عنوان IP، نوع المتصفح وإصداره، نوع الجهاز، نظام التشغيل، الصفحة المُحيلة، الصفحات التي تشاهدونها، ووقت الزيارة ومدتها، عبر ملفات تعريف الارتباط والتقنيات المشابهة.</li>
          <li><strong>عند أن تصبحوا عملاء —</strong> بيانات التواصل لموظفيكم الذين يعملون معنا، بيانات الفوترة، والمراسلات المتعلقة بالخدمات.</li>
          <li><strong>ما لا نجمعه —</strong> لا نجمع عمدًا بيانات شخصية حساسة عبر هذا الموقع، بما فيها بيانات الصحة، المعرّفات الحيوية، أو البيانات الجينية. لا ترسلوا بيانات حساسة عبر نماذج الموقع. إن تطلّب ارتباط عميل معالجة بيانات حساسة، نُنظّم ذلك باتفاق مكتوب منفصل ونحصل على أي تصريح لازم من الوزارة قبل بدء المعالجة.</li>
        </ul>
      </section>

      <section>
        <H2>4. لماذا نجمعها، وعلى أي أساس</H2>
        <Table headers={["الغرض", "لماذا", "الأساس القانوني"]} rows={LAWFUL_BASIS_ROWS_AR} />
        <p className="mt-4">
          <strong>الموافقة:</strong> حيث نعتمد على موافقتكم، تكون صريحة، محددة، ومُسجّلة. يمكنكم سحبها في أي وقت بالبريد <a href="mailto:support@wafa.om" className="underline">support@wafa.om</a>، ولا يؤثر السحب على المعالجة التي تمت قبله.
        </p>
      </section>

      <section>
        <H2>5. أين تُخزَّن بياناتكم</H2>
        <p className="mt-4">نُخزّن البيانات الشخصية على بنية تحتية يشغّلها مزوّدو الاستضافة والخدمات لدينا. الأدوات التي نستخدمها لتشغيل هذا الموقع ومراسلات أعمالنا هي:</p>
        <UL items={[
          <><strong>Namecheap Private Email</strong> — بريدنا التجاري و SMTP للردود على الاستفسارات. تُعالَج البيانات في بنية Namecheap التحتية.</>,
          <><strong>Google Analytics 4</strong> — تحليلات الموقع، تُحمَّل فقط بعد قبولكم ملفات تحليلات. تُعالَج البيانات في بنية Google التحتية.</>,
          <><strong>Emergent</strong> — المنصة التي يُستضاف عليها هذا الموقع. تُعالَج زيارات التطبيق في بنية Emergent التحتية.</>,
        ]} />
        <p className="mt-4">لا ندّعي استضافة داخل عُمان.</p>
        <p className="mt-4">حيث تُنقَل البيانات الشخصية خارج سلطنة عُمان:</p>
        <UL items={[
          "ننقل فقط ما هو ضروري لذلك الغرض المحدد؛",
          "نُقيّم مستوى الحماية المتاح في الدولة المستقبِلة قبل النقل؛",
          "نضع حماية تعاقدية مع المزوّد؛",
          "نحصل على موافقتكم حيث يشترط القانون ذلك؛ و",
          "لا ننقل البيانات الشخصية الحساسة خارج عُمان دون موافقة الوزارة.",
        ]} />
        <p className="mt-4">يمكنكم أن تطلبوا منا في أي وقت قائمة محدَّثة بأماكن معالجة بياناتكم على <a href="mailto:support@wafa.om" className="underline">support@wafa.om</a>.</p>
      </section>

      <section>
        <H2>6. مع من نُشاركها</H2>
        <p className="mt-4">لا نبيع بياناتكم الشخصية، ولا نُشاركها لأغراض تسويقية لأي طرف آخر. نُشاركها فقط مع:</p>
        <UL items={[
          "فريقنا، حيث يحتاجونها لأداء عملهم — فريقنا يعمل من عُمان والهند والولايات المتحدة والفلبين، ومُلزمون بالسرية؛",
          "مزوّدي الخدمات الذين يُساعدوننا على التشغيل، بعقود تُقيّد ما يمكنهم فعله بها؛",
          "المستشارين المهنيين مثل المحاسبين والمحامين عند الضرورة؛",
          "الجهات الرسمية، حيث نلتزم بالإفصاح بموجب القانون العُماني، أو لإثبات حق قانوني أو الدفاع عنه.",
        ]} />
      </section>

      <section>
        <H2>7. كم نحتفظ بها</H2>
        <Table headers={["الفئة", "مدة الاحتفاظ"]} rows={RETENTION_ROWS_AR} />
        <p className="mt-4">عند انتهاء مدة الاحتفاظ نحذف البيانات أو نُخفي هويتها بشكل لا رجعة فيه.</p>
      </section>

      <section>
        <H2>8. حقوقكم</H2>
        <p className="mt-4">بموجب قانون حماية البيانات الشخصية، لديكم الحق في:</p>
        <UL items={[
          "الاطلاع على كيفية معالجة بياناتكم الشخصية؛",
          "الوصول إلى بياناتكم الشخصية والحصول على نسخة؛",
          "تصحيح أو تحديث بيانات غير دقيقة أو غير مكتملة؛",
          "طلب الحذف؛",
          "سحب الموافقة في أي وقت حيث تعتمد المعالجة على الموافقة؛",
          "الاعتراض على المعالجة في الظروف التي يسمح بها القانون؛",
          "الإبلاغ بأي خرق للبيانات الشخصية يؤثر عليكم؛",
          "الشكوى للوزارة إن لم تكونوا راضين عن ردنا.",
        ]} />
        <p className="mt-4"><strong>كيف تمارسونها:</strong> راسلونا على <a href="mailto:support@wafa.om" className="underline">support@wafa.om</a> بما تريدونه وبمعلومات كافية لتحديد هويتكم. قد نطلب التحقق من هويتكم — هذا لحمايتكم.</p>
        <p className="mt-4"><strong>وقت الرد:</strong> نرد خلال خمسة وأربعين (45) يومًا من استلام طلبكم، كما تشترط اللائحة التنفيذية. لا نتقاضى مقابلًا لذلك.</p>
        <p className="mt-4">إن لم تكونوا راضين، يمكنكم الشكوى لدائرة حماية البيانات الشخصية في وزارة النقل والاتصالات وتقنية المعلومات. تواصلوا معنا أولًا إن أمكن — معظم القضايا أسرع في الحل مباشرة.</p>
      </section>

      <section>
        <H2>9. كيف نحميها</H2>
        <p className="mt-4">نطبّق تدابير تقنية وتنظيمية مناسبة للمخاطر، منها:</p>
        <UL items={[
          "تشفير البيانات أثناء النقل والتخزين؛",
          "التحكم بالوصول القائم على الأدوار ليرى الموظفون ما يحتاجونه فقط؛",
          "سجلات تدقيق للوصول للبيانات الشخصية؛",
          "المصادقة متعددة العوامل على الأنظمة الإدارية؛",
          "الترقيع المنتظم وإدارة الثغرات؛",
          "نسخ احتياطي تلقائي مع اختبار الاستعادة؛",
          "التزامات السرية والتدريب للموظفين؛",
          "تقييم أمني للموردين قبل التعاقد.",
        ]} />
        <p className="mt-4">لا نظام آمن تمامًا، ولا نزعم ذلك. ما نلتزم به هو حماية متناسبة، وإبلاغ صريح وسريع إن حدث خطأ.</p>
      </section>

      <section>
        <H2>10. إن حدث خرق</H2>
        <p className="mt-4">إن حدث خرق للبيانات الشخصية يُشكّل خطرًا على حقوقكم، نُبلغ الوزارة خلال اثنتين وسبعين (72) ساعة من علمنا به. حيث يُرجَّح أن يُسبّب الخرق ضررًا جسيمًا لكم، سنُبلغكم أيضًا، ونُخبركم بما حدث، وأي بيانات كانت متأثرة، وما نفعله حيالها، وما يجب أن تفعلوه. نحتفظ بسجل بخروقات البيانات الشخصية، متاح للوزارة عند الطلب.</p>
      </section>

      <section>
        <H2>11. ملفات تعريف الارتباط</H2>
        <p className="mt-4">راجعوا <a href="/ar/cookie-policy" className="underline">سياسة ملفات تعريف الارتباط</a>.</p>
      </section>

      <section>
        <H2>12. الأطفال</H2>
        <p className="mt-4">هذا الموقع ليس موجهًا للأطفال. لا نجمع عمدًا بيانات شخصية من أي شخص دون سن 18 عبر هذا الموقع. حيث يشمل مشروع عميل بيانات أطفال، نُنظّم ذلك بشكل منفصل، بما في ذلك الحصول على موافقة ولي الأمر كما تشترط اللائحة التنفيذية. إن اعتقدتم أن طفلًا قدّم لنا بيانات شخصية، تواصلوا معنا وسنحذفها.</p>
      </section>

      <section>
        <H2>13. الروابط لمواقع أخرى</H2>
        <p className="mt-4">يربط موقعنا لمواقع أخرى لا نُسيطر عليها. لا تنطبق هذه السياسة عليها.</p>
      </section>

      <section>
        <H2>14. التغييرات</H2>
        <p className="mt-4">قد نُحدّث هذه السياسة. التاريخ في الأعلى يُبيّن آخر تغيير. حيث يؤثر التغيير جوهريًا على حقوقكم، سنُخبركم مباشرة.</p>
      </section>

      <section>
        <H2>15. التواصل</H2>
        <p className="mt-4">وفا سوليوشنز LLC، سلطنة عُمان.<br />البريد <a href="mailto:support@wafa.om" className="underline">support@wafa.om</a>، أو استخدموا زر "تحدّثوا معنا عبر واتساب" على أي صفحة.</p>
      </section>
    </LegalShell>
  );
}

export function TermsOfServiceAr() {
  return (
    <LegalShell
      testid="terms-ar"
      title="شروط الخدمة"
      description="الشروط التي تحكم استخدام موقع وفا سوليوشنز."
      h1="شروط الخدمة"
      intro="تحكم هذه الشروط استخدام هذا الموقع فقط. تخضع مشاريع العملاء لاتفاقية موقّعة منفصلة، وتغلب الأخيرة عند الاختلاف."
    >
      <section>
        <H2>1. هذه الشروط</H2>
        <p className="mt-4">تحكم هذه الشروط استخدامكم للموقع. باستخدامه، تقبلونها. قد نُحدّثها؛ التاريخ أعلاه يُبيّن آخر تغيير.</p>
      </section>

      <section>
        <H2>2. من نحن</H2>
        <p className="mt-4">وفا سوليوشنز LLC، شركة تقنية مسجّلة في سلطنة عُمان تحت رقم السجل التجاري {CR_NUMBER}، والشريك التقني لمجموعة Altalib.</p>
      </section>

      <section>
        <H2>3. استخدام الموقع</H2>
        <p className="mt-4">يمكنكم استخدام هذا الموقع لأغراض مشروعة تتعلق بتقييم خدماتنا أو تلقّيها. لا يجوز لكم:</p>
        <UL items={[
          "استخدام الموقع بأي شكل يخالف القانون العُماني؛",
          "محاولة الوصول غير المصرح به لأي جزء من الموقع، خوادمه، أو الأنظمة المتصلة؛",
          "إدخال كود ضار أو التدخل في تشغيل الموقع؛",
          "استخراج المحتوى أو حصده أو استخراجه بشكل منهجي؛",
          "استخدام الموقع لإرسال مراسلات تجارية غير مطلوبة؛",
          "تحريف هويتكم أو انتمائكم.",
        ]} />
        <p className="mt-4">قد نُعلّق أو نسحب الوصول دون إشعار.</p>
      </section>

      <section>
        <H2>4. المعلومات على هذا الموقع</H2>
        <p className="mt-4">نحرص على إبقاء الموقع دقيقًا، لكن دون ضمان اكتماله أو دقته أو تحديثه.</p>
        <p className="mt-4"><strong>لا شيء على هذا الموقع نصيحة مهنية</strong> — مقالاتنا وأدلتنا، بما فيها ما نُنشر عن قانون حماية البيانات أو الحوكمة والمخاطر، معلومات عامة، لا استشارة قانونية أو تنظيمية أو مالية أو تقنية لظروفكم.</p>
        <p className="mt-4"><strong>لا شيء على هذا الموقع عرضًا ملزمًا</strong> — الأسعار المنشورة والجداول الزمنية ونطاقات التكلفة إرشادية، والتزام مُلزم لا ينشأ إلا من عرض مكتوب موقّع من الطرفين.</p>
      </section>

      <section>
        <H2>5. حقوق ملكيتنا الفكرية</H2>
        <p className="mt-4">جميع المحتوى على هذا الموقع مملوك لوفا سوليوشنز LLC أو مرخّص لها ومحمي بموجب القانون العُماني والمعاهدات الدولية. "وفا سوليوشنز" و "Meyar"، مع أسماء منتجاتنا الأخرى وشعاراتنا، هي علاماتنا؛ لا يجوز استخدامها دون إذن مكتوب.</p>
        <p className="mt-4">يمكنكم قراءة الموقع، وطباعة أو تنزيل مقتطفات لمرجعكم الشخصي، ومشاركة الروابط.</p>
        <p className="mt-4">لا يجوز لكم:</p>
        <UL items={[
          "إعادة إنتاج أو نشر أو توزيع محتوانا تجاريًا؛",
          "إزالة الإسناد؛",
          "استخدام محتوانا لتدريب نموذج تعلم آلي دون إذن مكتوب؛",
          "تقديم محتوانا كأنه لكم.",
        ]} />
      </section>

      <section>
        <H2>6. الملكية الفكرية في أعمال العملاء</H2>
        <p className="mt-4"><em>هذا ملخص فقط؛ اتفاقية مشروعكم الموقّعة هي التي تحكم.</em></p>
        <p className="mt-4">عند السداد الكامل لجميع المبالغ المستحقة، يصبح كود التطبيق المخصص والتصاميم والتوثيق التي أنشأناها خصيصًا لكم ملكًا لكم.</p>
        <p className="mt-4">نبني على أُطرنا ومكتباتنا ومكوّناتنا المُطوّرة مسبقًا، والتي طُوّرت على حسابنا وتُعاد استخدامها عبر العملاء؛ تبقى ملكًا لنا، وتحصلون على ترخيص دائم غير حصري وغير قابل للنقل لاستخدامها كجزء من الحل المُسلَّم. هذا ما يُبقي سعر مشروعكم منخفضًا — أنتم لا تدفعون لإعادة بناء ما هو موجود.</p>
        <p className="mt-4">Meyar ومنصاتنا الأخرى منتجات ملكنا، مُرخّصة بشروطها الخاصة؛ التعاقد معنا لخدمات لا يمنحكم حقوقًا فيها.</p>
        <p className="mt-4">بعض الحلول تشمل مكوّنات مفتوحة المصدر أو مُرخّصة من طرف ثالث تحكمها تراخيصها الخاصة، وسنُبيّنها لكم. لن نذكر اسمكم أو نصف مشروعكم علنًا دون إذن مكتوب.</p>
      </section>

      <section>
        <H2>7. مُدخلاتكم</H2>
        <p className="mt-4">إن أرسلتم لنا مواد عبر الموقع، فأنتم تُقرّون بأن لديكم الحق في إرسالها، وتمنحوننا إذنًا لاستخدامها للغرض الذي أرسلتموها له. لا ترسلوا معلومات سرية عبر الموقع. إن احتجتم للسرية قبل وصف مشروعكم، اطلبوا اتفاقية عدم إفصاح — نوقّعها بشكل روتيني ودون تكلفة.</p>
      </section>

      <section>
        <H2>8. المواقع المرتبطة</H2>
        <p className="mt-4">نرتبط بمواقع أخرى للراحة. لا نُسيطر عليها ولا نُصادق عليها ولا نتحمل مسؤولية محتواها أو تعاملها مع بياناتكم.</p>
      </section>

      <section>
        <H2>9. التوفّر</H2>
        <p className="mt-4">نسعى لإبقاء الموقع متاحًا، لكن دون ضمان وصول متواصل، وقد نُعلّقه أو نُقيّده دون إشعار.</p>
      </section>

      <section>
        <H2>10. المسؤولية</H2>
        <p className="mt-4">لا شيء في هذه الشروط يستبعد أو يحد من المسؤولية حيث لا يسمح القانون بالاستبعاد، بما في ذلك الوفاة أو الإصابة الشخصية الناجمة عن الإهمال، أو الاحتيال.</p>
        <p className="mt-4">مع مراعاة ذلك، وإلى أقصى حد يسمح به القانون العُماني:</p>
        <UL items={[
          "يُقدَّم الموقع \"كما هو\" دون ضمانات من أي نوع؛",
          "لسنا مسؤولين عن خسارة أرباح، أعمال، إيرادات، وفورات متوقعة، أو بيانات، أو أي خسارة غير مباشرة أو تبعية ناتجة عن استخدامكم للموقع؛",
          "إجمالي مسؤوليتنا فيما يتعلق باستخدامكم للموقع محدودة بمبلغ 100 ريال عُماني.",
        ]} />
        <p className="mt-4">تُحكم المسؤولية في مشاريع العملاء بشكل منفصل باتفاقيتكم الموقّعة.</p>
      </section>

      <section>
        <H2>11. الخصوصية</H2>
        <p className="mt-4">راجعوا <a href="/ar/privacy-policy" className="underline">سياسة الخصوصية</a> و <a href="/ar/cookie-policy" className="underline">سياسة ملفات تعريف الارتباط</a>.</p>
      </section>

      <section>
        <H2>12. القانون الحاكم والاختصاص القضائي</H2>
        <p className="mt-4">تخضع هذه الشروط، وأي نزاع ينشأ عنها، لقوانين سلطنة عُمان. لمحاكم سلطنة عُمان الاختصاص الحصري.</p>
      </section>

      <section>
        <H2>13. أحكام عامة</H2>
        <p className="mt-4">إن وُجد أن أي حكم غير قابل للتنفيذ، تستمر البقية سارية. عدم إنفاذنا لبند ليس تنازلًا عنه. تُشكّل هذه الشروط الاتفاقية الكاملة بيننا فيما يتعلق باستخدام الموقع.</p>
      </section>

      <section>
        <H2>14. التواصل</H2>
        <p className="mt-4">وفا سوليوشنز LLC، سلطنة عُمان.<br /><a href="mailto:support@wafa.om" className="underline">support@wafa.om</a></p>
      </section>
    </LegalShell>
  );
}

const COOKIE_ROWS_AR = [
  ["wafa.cookie-consent.v1", "ضروري بشدة", "يُسجّل اختياركم لملفات تعريف الارتباط لكيلا نسأل مجددًا على كل صفحة.", "12 شهرًا"],
  ["wafa.lang.v1 (قديم)", "ضروري بشدة", "حفظ تفضيل اللغة قبل أن تولّى بادئة /ar/ ذلك. مُحتفظ به فقط للزوّار السابقين.", "12 شهرًا"],
  ["wafa.chat.session.v1", "ضروري بشدة", "يُبقي محادثة الدردشة المباشرة مرتبطة بكم عبر إعادات تحميل الصفحة.", "الجلسة"],
  ["ملفات تحليلات GA4 (تُضبط بعد الموافقة فقط)", "تحليلات", "يستخدم Google Analytics 4 ملفات تعريف طرف أول (عادةً _ga و _ga_<container-id>) لقياس مشاهدات الصفحات والزوار العائدين. تُحمَّل فقط إن قبلتم ملفات التحليلات.", "13 شهرًا"],
];

export function CookiePolicyAr() {
  return (
    <LegalShell
      testid="cookie-policy-ar"
      title="سياسة ملفات تعريف الارتباط"
      description="كيف تستخدم وفا سوليوشنز LLC ملفات تعريف الارتباط وما هي خياراتكم."
      h1="سياسة ملفات تعريف الارتباط"
    >
      <section>
        <H2>ما هي ملفات تعريف الارتباط</H2>
        <p className="mt-4">ملفات تعريف الارتباط ملفات نصية صغيرة يُخزّنها الموقع على جهازكم. تُتيح للموقع تذكّر أشياء — تفضيل اللغة، ما إن كنتم مسجّلين، كيف وصلتم — وتُتيح لنا فهم كيفية استخدام الموقع.</p>
      </section>

      <section>
        <H2>كيف نستخدمها</H2>
        <ul className="mt-4 space-y-3">
          <li><strong>ملفات ضرورية بشدة</strong> تُشغّل الموقع: الأمان، إرسال النماذج، وتذكّر اختياراتكم. لا يعمل الموقع بدونها، لذا لا تتطلب موافقتكم.</li>
          <li><strong>ملفات التحليلات</strong> تُخبرنا أي الصفحات تُقرأ، كيف يصل الناس، وأين يغادرون؛ تعمل فقط إن قبلتموها.</li>
          <li><strong>ملفات التسويق</strong> تُتيح قياس إعلاناتنا؛ تعمل فقط إن قبلتموها.</li>
        </ul>
        <p className="mt-4">لا نستخدم ملفات تعريف الارتباط لبناء ملف شخصي عنكم، ولا نبيع بيانات هذه الملفات.</p>
      </section>

      <section>
        <H2>الملفات التي نضبطها</H2>
        <Table headers={["الملف", "النوع", "الغرض", "المدة"]} rows={COOKIE_ROWS_AR} />
      </section>

      <section>
        <H2>خياراتكم</H2>
        <p className="mt-4">عند زيارتكم الأولى، نسأل قبل ضبط أي شيء تجاوز الملفات الضرورية بشدة. يمكنكم تغيير رأيكم في أي وقت عبر رابط <button type="button" className="underline hover:text-wafa-deep" onClick={() => window.dispatchEvent(new CustomEvent("wafa:open-cookie-settings"))}>إعدادات ملفات تعريف الارتباط</button> في التذييل، أو بحذفها في متصفحكم. حذف الملفات الضرورية قد يُعطّل أجزاء من الموقع.</p>
      </section>

      <section>
        <H2>الأطراف الثالثة</H2>
        <p className="mt-4">بعض الملفات تضبطها الخدمات التي نستخدمها، المذكورة أعلاه. لهؤلاء المزوّدين سياساتهم الخاصة، وحيث يُعالجون البيانات خارج عُمان، نتعامل مع ذلك كما هو مُوضَّح في القسم 5 من <a href="/ar/privacy-policy" className="underline">سياسة الخصوصية</a>.</p>
      </section>

      <section>
        <H2>التواصل</H2>
        <p className="mt-4"><a href="mailto:support@wafa.om" className="underline">support@wafa.om</a></p>
      </section>
    </LegalShell>
  );
}
